"use client";

import { useMemo } from "react";
import { cn } from "@/lib/utils";
import type { LegalDocumentSection } from "./types";
import { useSectionNav } from "./useSectionNav";

type SectionNavProps = {
  sections: LegalDocumentSection[];
};

export const SectionNav = ({ sections }: SectionNavProps) => {
  const flatSections = useMemo(() => {
    const flatten = (
      arr: LegalDocumentSection[],
      level = 0,
    ): (LegalDocumentSection & { level: number })[] => {
      return arr.reduce(
        (acc, curr) => {
          acc.push({ ...curr, level });
          if (curr.subsections) {
            acc.push(...flatten(curr.subsections, level + 1));
          }
          return acc;
        },
        [] as (LegalDocumentSection & { level: number })[],
      );
    };
    return flatten(sections);
  }, [sections]);

  const { activeId, handleNavigate } = useSectionNav(flatSections);

  return (
    <nav className="border-l border-[#EDEEEE]/80">
      <ol className="space-y-1 py-2">
        {flatSections.map((section) => {
          const isActive = activeId === section.id;

          return (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                onClick={(event) => {
                  event.preventDefault();
                  handleNavigate(section.id);
                }}
                style={{ paddingLeft: `${1 + section.level * 1}rem` }}
                className={cn(
                  "relative flex items-center py-2.5 pr-4 text-base transition-colors font-semibold",
                  isActive
                    ? "font-semibold text-[#0B9BFF]"
                    : "text-[#898D91] hover:text-[#0B9BFF]",
                )}
              >
                {/* Active Indicator Bar */}
                {isActive ? (
                  <span className="absolute -left-[1px] top-1/2 h-11 w-[3px] -translate-y-1/2 rounded-full bg-[#0B9BFF] shadow-[1px_0_8px_rgba(11,155,255,0.4)]" />
                ) : null}

                {/* Text Content */}
                <span className="line-clamp-2">{section.title}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
